// Urban Unity's own KPI targets for the Webinars tab cards (Tab for Joey Forslund, Oct 29 2026).
// Joey, Oct 29 2026 3:15 PM ET: "we want to base our KPI not on what Cr8Her targets are ... what are the Urban Unity
// targets? They are established because of the information that I gave Jeremy Haynes AI on our last 6 months of data."
// The Webinars tab copies Cr8Her's layout 1:1, but every target badge reads from config/velocity-targets.ts (Jeremy
// Haynes AI's $200K/mo model on UU's Apr 27 – Oct 25, 2026 data, Jeremy plan), never Cr8Her's benchmark ranges.
// Edit a number in velocity-targets.ts and the Webinars cards, Book a call funnel and Velocity Metrics all follow.
//
// Volume rows are Jeremy's WEEKLY webinar-funnel targets; UU runs one webinar a week, so one webinar is scored against
// one week's target. Rates and averages are fixed levels. Text ranges ("20–30%") become a range.
import { VELOCITY_TARGET_ROWS, computePlan, type VelocityTargetRow } from "./velocity-targets";

export type UuTargetRange = { lo: number; hi: number; unit: "%" | "$" | "min" | "n"; dir: "min" | "max" | "range" };
export type UuTargetMap = Record<string, { cold: UuTargetRange; warm: UuTargetRange }>;

const plan = computePlan("jeremy");

function toRange(row: VelocityTargetRow | undefined): UuTargetRange | null {
  if (!row) return null;
  const t = row.target;
  const unit: UuTargetRange["unit"] = row.kind === "rate" ? "%" : row.kind === "money" || row.kind === "avg" ? "$" : row.kind === "minutes" ? "min" : "n";
  if (t.type === "volume" || t.type === "level") {
    const v = t.value(plan);
    if (!Number.isFinite(v) || t.dir === "none") return null;
    return { lo: v, hi: v, unit, dir: t.dir === "max" ? "max" : "min" };
  }
  const text = typeof t.text === "function" ? t.text(plan) : t.text;
  const range = text.match(/(\d+(?:\.\d+)?)\s*[–-]\s*(\d+(?:\.\d+)?)/);
  if (range) return { lo: Number(range[1]), hi: Number(range[2]), unit, dir: "range" };
  const single = text.match(/(\d+(?:\.\d+)?)/);
  if (!single) return null;
  const v = Number(single[1]);
  return { lo: v, hi: v, unit, dir: /earlier|under|or less/i.test(text) || t.max != null ? "max" : "min" };
}

function build(section: string, labels: Record<string, string>): UuTargetMap {
  const rows = VELOCITY_TARGET_ROWS.webinar.filter((r) => r.section === section);
  const out: UuTargetMap = {};
  for (const [card, rowLabel] of Object.entries(labels)) {
    const r = toRange(rows.find((x) => x.label === rowLabel));
    if (r) out[card] = { cold: r, warm: r };
  }
  return out;
}

/** Sales Metrics cards (card label → Jeremy row label). */
export const UU_WEBINAR_SALES_TARGETS = build("Sales Metrics", {
  "Opt-ins": "Opt-ins",
  "Opt-in Rate": "Opt-in Rate",
  "Total Apps": "Total Apps",
  "Full Apps": "Full Apps",
  "Qualified Apps": "Qualified Apps",
  "Webinar Bookings": "Webinar Bookings",
  "End of Call Reports Filled": "EOCs Filled",
  "Calls Taken": "Calls Taken",
  "Show Rate": "Show Rate",
  "No Shows": "No Shows",
  "Closes": "Closes",
  "Deposits": "Deposits",
  "Close Rate": "Close Rate",
  "Cash": "Cash",
  "AOV": "AOV",
  "Cash/Call": "Cash/Call",
  "Full App→Booking": "Full App to Booking",
  "Optin→Booking": "Opt-in to Booking",
  "Opt-in→Full App": "Opt-in to Full App",
  "Optin→Qual App": "Opt-in to Qual App",
  "Qual→Booking": "Qual to Booking",
  "Add to Calendar": "Add to Calendar",
  "Joined WhatsApp": "Joined WhatsApp",
});

/** On-Webinar Metrics cards. */
export const UU_WEBINAR_ON_WEBINAR_TARGETS = build("On-Webinar Metrics", {
  "Opt-ins": "Opt-ins",
  "Attended": "Attended",
  "Show Rate": "Show Rate",
  "Avg Duration": "Avg Duration",
  "Pitch Start": "Pitch Start",
  "@ Pitch": "@ Pitch",
  "Booking Rate": "Booking Rate",
  "Total App Rate": "Total App Rate",
  "Full App Rate": "Full App Rate",
  "Qual App Rate": "Qual App Rate",
});

/** Zoom Attendance rows (list view). */
export const UU_WEBINAR_ZOOM_TARGETS = build("Zoom Attendance", {
  "Registrants": "Registrants",
  "Attended": "Attended",
  "Show Rate": "Show Rate",
  "Avg Duration": "Avg Duration",
  "4d before": "Registered 4 days before",
  "3d before": "Registered 3 days before",
  "2d before": "Registered 2 days before",
  "1d before": "Registered 1 day before",
  "Day of": "Registered day of",
});
